import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";

import WhatsAppIconWhite from "../../assets/logoLoginWhite.png";
import WhatsAppIcon from "../../assets/logologinnovo.png";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: "100vh",
    overflow: "hidden",
  },
  brandPanel: {
    flex: "0 0 45%",
    background: "linear-gradient(160deg, #1a73d7 0%, #0d47a1 100%)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  decorCircle1: {
    position: "absolute",
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    top: -80,
    right: -80,
  },
  decorCircle2: {
    position: "absolute",
    width: 220,
    height: 220,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
    bottom: 40,
    left: -60,
  },
  decorCircle3: {
    position: "absolute",
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    bottom: 180,
    right: 40,
  },
  brandContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    maxWidth: 360,
  },
  brandLogo: {
    height: 72,
    width: "auto",
    marginBottom: theme.spacing(4),
    filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.2))",
  },
  brandTagline: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.75rem",
    lineHeight: 1.3,
    marginBottom: theme.spacing(2),
  },
  brandSubtitle: {
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.95rem",
    lineHeight: 1.6,
  },
  brandDivider: {
    width: 48,
    height: 3,
    background: "rgba(255,255,255,0.4)",
    borderRadius: 2,
    margin: `${theme.spacing(3)}px auto`,
  },
  formPanel: {
    flex: "0 0 55%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      flex: "0 0 100%",
    },
  },
  formWrapper: {
    width: "100%",
    maxWidth: 400,
  },
  formHeader: {
    marginBottom: theme.spacing(4),
  },
  formTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  formSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  mobileLogo: {
    display: "none",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  mobileLogoImg: {
    height: 48,
    width: "auto",
  },
  form: {
    width: "100%",
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: "1rem",
    borderRadius: theme.spacing(1),
    boxShadow: "0px 4px 14px rgba(26, 115, 215, 0.4)",
    textTransform: "none",
  },
  backLink: {
    display: "block",
    textAlign: "center",
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  successBox: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(26, 115, 215, 0.15)"
        : "#e8f0fe",
    border: "1px solid #1a73d7",
    marginBottom: theme.spacing(2),
  },
  successText: {
    color: theme.palette.type === "dark" ? "#90caf9" : "#1a73d7",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error("Erro ao processar solicitação. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <CssBaseline />

      <div className={classes.brandPanel}>
        <div className={classes.decorCircle1} />
        <div className={classes.decorCircle2} />
        <div className={classes.decorCircle3} />

        <div className={classes.brandContent}>
          <img
            src={WhatsAppIconWhite}
            alt="Message Express"
            className={classes.brandLogo}
          />
          <div className={classes.brandDivider} />
          <Typography variant="h5" className={classes.brandTagline}>
            Atendimento inteligente, resultados reais.
          </Typography>
          <Typography variant="body2" className={classes.brandSubtitle}>
            Gerencie conversas, equipes e campanhas em um só lugar — com a
            agilidade que o seu negócio precisa.
          </Typography>
        </div>
      </div>

      <div className={classes.formPanel}>
        <div className={classes.formWrapper}>
          <div className={classes.mobileLogo}>
            <img
              src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
              alt="Message Express"
              className={classes.mobileLogoImg}
            />
          </div>

          <div className={classes.formHeader}>
            <Typography variant="h5" className={classes.formTitle}>
              Esqueci minha senha
            </Typography>
            <Typography variant="body2" className={classes.formSubtitle}>
              {sent
                ? "Verifique seu e-mail"
                : "Informe seu e-mail para receber o link de redefinição"}
            </Typography>
          </div>

          {sent ? (
            <>
              <div className={classes.successBox}>
                <Typography className={classes.successText}>
                  Se o e-mail <strong>{email}</strong> estiver cadastrado, você
                  receberá as instruções para redefinir sua senha em breve.
                  Verifique também a caixa de spam.
                </Typography>
              </div>
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="primary"
                className={classes.backLink}
              >
                Voltar para o login
              </Link>
            </>
          ) : (
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                fullWidth
                id="email"
                label="E-mail"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
                type="email"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submitButton}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : null
                }
              >
                {loading ? "Enviando..." : "Enviar link de redefinição"}
              </Button>

              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="primary"
                className={classes.backLink}
              >
                Voltar para o login
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
